import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
    DefaultButton,
    Dialog,
    DialogFooter,
    DialogType,
    Icon,
    IconButton,
    MessageBar,
    MessageBarType,
    PrimaryButton,
    Separator,
    Spinner,
    SpinnerSize,
    Stack,
    Text,
    TextField,
} from '@fluentui/react';
import { ThemeContext } from '../../../../HubLayout/models/ThemeContext';
import { getThemeFromString } from '../../../../Common/util/localStorageUtils';
import { usePromise } from '../../../../Common/hooks/usePromise';
import {
    createContainer,
    deleteContainer,
    getContainerStatus,
} from '../../../../AGSS/api/AGSSApi';
import {
    CacheContainerActionResultDto,
    CacheContainerStatusDto,
} from '../../../../AGSS/models/CacheContainer';
import { JsonResult } from '../../../../HubLayout/components/Utilities/JsonResult';

interface IManageHydrationCacheContainerProps {}

export const ManageHydrationCacheContainer: React.FC<
    IManageHydrationCacheContainerProps
> = () => {
    const themeContext = useContext(ThemeContext);
    const theme = getThemeFromString(themeContext.themeName);
    const stackTokens = { childrenGap: 16 };

    // Status state
    const [status, setStatus] = useState<CacheContainerStatusDto | undefined>();
    const [statusError, setStatusError] = useState<string | undefined>();
    const [statusLoading, setStatusLoading] = useState<boolean>(false);

    // Action state
    const [actionLoading, setActionLoading] = useState<boolean>(false);
    const [lastResult, setLastResult] = useState<
        CacheContainerActionResultDto | undefined
    >();
    const [actionError, setActionError] = useState<string | undefined>();

    // Confirmation dialog
    const [confirmAction, setConfirmAction] = useState<
        'delete' | 'create' | undefined
    >();
    const [confirmInput, setConfirmInput] = useState<string>('');

    // Preserved indexing policy from a prior delete (auto-captured from status)
    const [savedIndexingPolicyJson, setSavedIndexingPolicyJson] = useState<
        string | undefined
    >();

    // ── Status fetch ──────────────────────────────────────────────
    const fetchStatus = useCallback(async () => {
        setStatusLoading(true);
        setStatusError(undefined);
        try {
            const result = await getContainerStatus();
            setStatus(result);
        } catch (err: any) {
            setStatusError(
                err?.response?.data?.message ??
                    err?.message ??
                    'Failed to fetch container status'
            );
        } finally {
            setStatusLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchStatus();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Delete ────────────────────────────────────────────────────
    const handleDelete = useCallback(async () => {
        setActionLoading(true);
        setActionError(undefined);
        setLastResult(undefined);
        // Capture indexing policy before we nuke
        if (status?.indexingPolicyJson) {
            setSavedIndexingPolicyJson(status.indexingPolicyJson);
        }
        try {
            const result = await deleteContainer();
            setLastResult(result);
            if (result.status) {
                setStatus(result.status);
            } else {
                await fetchStatus();
            }
        } catch (err: any) {
            setActionError(
                err?.response?.data?.message ??
                    err?.message ??
                    'Delete failed'
            );
            await fetchStatus();
        } finally {
            setActionLoading(false);
        }
    }, [status, fetchStatus]);

    // ── Create ────────────────────────────────────────────────────
    const handleCreate = useCallback(async () => {
        setActionLoading(true);
        setActionError(undefined);
        setLastResult(undefined);
        try {
            const result = await createContainer(
                savedIndexingPolicyJson ?? undefined
            );
            setLastResult(result);
            if (result.status) {
                setStatus(result.status);
            } else {
                await fetchStatus();
            }
        } catch (err: any) {
            const status = err?.response?.status;
            const msg =
                err?.response?.data?.message ??
                err?.message ??
                'Create failed';
            setActionError(
                status === 409
                    ? `Container already exists with items — delete it first. (${msg})`
                    : msg
            );
            await fetchStatus();
        } finally {
            setActionLoading(false);
        }
    }, [savedIndexingPolicyJson, fetchStatus]);

    // ── Confirmation dialog ───────────────────────────────────────
    const openConfirm = (action: 'delete' | 'create') => {
        setConfirmInput('');
        setConfirmAction(action);
    };

    const closeConfirm = () => {
        setConfirmAction(undefined);
        setConfirmInput('');
    };

    const executeConfirmed = () => {
        closeConfirm();
        if (confirmAction === 'delete') {
            handleDelete();
        } else if (confirmAction === 'create') {
            handleCreate();
        }
    };

    const containerName = status?.containerName ?? '';
    const confirmValid =
        confirmInput.toLowerCase() === containerName.toLowerCase() &&
        confirmInput.length > 0;

    // ── Render helpers ────────────────────────────────────────────
    const fieldRow = (label: string, value: string | null | undefined) => (
        <Stack
            horizontal
            tokens={{ childrenGap: 8 }}
            verticalAlign="center"
            styles={{ root: { minHeight: 28 } }}
        >
            <Text
                variant="small"
                styles={{
                    root: {
                        fontWeight: 600,
                        width: 140,
                        flexShrink: 0,
                        color: theme.palette.neutralSecondary,
                    },
                }}
            >
                {label}
            </Text>
            <Text variant="small">{value ?? '—'}</Text>
        </Stack>
    );

    return (
        <Stack tokens={stackTokens}>
            {/* ── Zone 1: Status Panel ─────────────────────────────── */}
            <div
                style={{
                    padding: '16px',
                    backgroundColor: theme.palette.white,
                    borderRadius: '4px',
                    border: `1px solid ${theme.palette.neutralTertiaryAlt}`,
                }}
            >
                <Stack
                    horizontal
                    verticalAlign="center"
                    horizontalAlign="space-between"
                    styles={{ root: { marginBottom: 12 } }}
                >
                    <Stack
                        horizontal
                        verticalAlign="center"
                        tokens={{ childrenGap: 8 }}
                    >
                        <Icon
                            iconName="Database"
                            styles={{
                                root: {
                                    fontSize: 16,
                                    color: theme.palette.themePrimary,
                                },
                            }}
                        />
                        <Text
                            variant="mediumPlus"
                            styles={{ root: { fontWeight: 600 } }}
                        >
                            Container Status
                        </Text>
                    </Stack>
                    <DefaultButton
                        text="Refresh Status"
                        iconProps={{ iconName: 'Refresh' }}
                        onClick={fetchStatus}
                        disabled={statusLoading || actionLoading}
                    />
                </Stack>

                {statusLoading && !status && (
                    <Spinner
                        size={SpinnerSize.medium}
                        label="Loading status…"
                    />
                )}

                {statusError && (
                    <MessageBar messageBarType={MessageBarType.error}>
                        {statusError}
                    </MessageBar>
                )}

                {status && (
                    <Stack tokens={{ childrenGap: 4 }}>
                        <Stack
                            horizontal
                            verticalAlign="center"
                            tokens={{ childrenGap: 8 }}
                            styles={{ root: { minHeight: 28 } }}
                        >
                            <Text
                                variant="small"
                                styles={{
                                    root: {
                                        fontWeight: 600,
                                        width: 140,
                                        flexShrink: 0,
                                        color: theme.palette.neutralSecondary,
                                    },
                                }}
                            >
                                Exists
                            </Text>
                            <Icon
                                iconName={
                                    status.exists
                                        ? 'SkypeCircleCheck'
                                        : 'SkypeCircleMinus'
                                }
                                styles={{
                                    root: {
                                        color: status.exists
                                            ? theme.palette.green
                                            : theme.palette.red,
                                        fontSize: 16,
                                    },
                                }}
                            />
                            <Text variant="small">
                                {status.exists ? 'Yes' : 'No'}
                            </Text>
                        </Stack>
                        <Stack
                            horizontal
                            tokens={{ childrenGap: 8 }}
                            verticalAlign="center"
                            styles={{ root: { minHeight: 28 } }}
                        >
                            <Text
                                variant="small"
                                styles={{
                                    root: {
                                        fontWeight: 600,
                                        width: 140,
                                        flexShrink: 0,
                                        color: theme.palette.neutralSecondary,
                                    },
                                }}
                            >
                                Container
                            </Text>
                            <Text variant="small">{status.containerName ?? '—'}</Text>
                            {status.containerName && (
                                <IconButton
                                    iconProps={{ iconName: 'Copy' }}
                                    title="Copy container name to clipboard"
                                    ariaLabel="Copy container name"
                                    styles={{ root: { width: 24, height: 24 }, icon: { fontSize: 12 } }}
                                    onClick={() => navigator.clipboard.writeText(status.containerName!)}
                                />
                            )}
                        </Stack>
                        {fieldRow('Database', status.databaseName)}
                        {fieldRow('Account', status.account)}
                        {fieldRow('Partition Key', status.partitionKey)}
                        {fieldRow(
                            'Max Throughput',
                            status.maxThroughput != null
                                ? `${status.maxThroughput.toLocaleString()} RU/s`
                                : null
                        )}
                        {fieldRow('Indexing Mode', status.indexingMode)}
                        {fieldRow(
                            'Approx Item Count',
                            status.approxItemCount != null
                                ? status.approxItemCount.toLocaleString()
                                : null
                        )}
                    </Stack>
                )}
            </div>

            {/* ── Zone 2: Action Buttons ───────────────────────────── */}
            <Stack horizontal tokens={{ childrenGap: 12 }}>
                <PrimaryButton
                    text="Delete Container"
                    iconProps={{ iconName: 'Delete' }}
                    onClick={() => openConfirm('delete')}
                    disabled={
                        actionLoading ||
                        statusLoading ||
                        !status ||
                        !status.exists
                    }
                    styles={{
                        root: {
                            backgroundColor: theme.palette.red,
                            borderColor: theme.palette.red,
                        },
                        rootHovered: {
                            backgroundColor: theme.palette.redDark,
                            borderColor: theme.palette.redDark,
                        },
                        rootDisabled: {
                            backgroundColor: theme.palette.neutralLighter,
                            borderColor: theme.palette.neutralLighter,
                        },
                    }}
                />
                <PrimaryButton
                    text="Create Container"
                    iconProps={{ iconName: 'Add' }}
                    onClick={() => openConfirm('create')}
                    disabled={
                        actionLoading ||
                        statusLoading ||
                        !status ||
                        status.exists
                    }
                />
            </Stack>

            {actionLoading && (
                <Spinner
                    size={SpinnerSize.medium}
                    label={
                        confirmAction === 'delete' || lastResult === undefined
                            ? 'Processing…'
                            : 'Processing…'
                    }
                />
            )}

            {/* ── Zone 3: Last Action Result ───────────────────────── */}
            {actionError && (
                <MessageBar messageBarType={MessageBarType.error}>
                    {actionError}
                </MessageBar>
            )}

            {lastResult && (
                <div
                    style={{
                        padding: '12px',
                        backgroundColor: theme.palette.white,
                        borderRadius: '4px',
                        border: `1px solid ${theme.palette.neutralTertiaryAlt}`,
                    }}
                >
                    <Stack tokens={{ childrenGap: 4 }}>
                        <MessageBar messageBarType={MessageBarType.success}>
                            {lastResult.action} completed on{' '}
                            <strong>{lastResult.containerName}</strong> in{' '}
                            {lastResult.durationMs}ms
                            {lastResult.priorItemCount != null &&
                                lastResult.priorItemCount > 0 &&
                                ` (${lastResult.priorItemCount.toLocaleString()} items prior)`}
                        </MessageBar>
                        <JsonResult data={lastResult} />
                    </Stack>
                </div>
            )}

            {/* ── Confirmation Dialog ──────────────────────────────── */}
            <Dialog
                hidden={!confirmAction}
                onDismiss={closeConfirm}
                dialogContentProps={{
                    type: DialogType.normal,
                    title:
                        confirmAction === 'delete'
                            ? 'Delete Container'
                            : 'Create Container',
                    subText: `Type the container name to confirm: ${containerName}`,
                }}
                modalProps={{ isBlocking: true }}
            >
                <TextField
                    placeholder={containerName}
                    value={confirmInput}
                    onChange={(_, v) => setConfirmInput(v ?? '')}
                />
                <DialogFooter>
                    <PrimaryButton
                        text="Confirm"
                        onClick={executeConfirmed}
                        disabled={!confirmValid}
                    />
                    <DefaultButton text="Cancel" onClick={closeConfirm} />
                </DialogFooter>
            </Dialog>
        </Stack>
    );
};
